import React from 'react';
import { Input } from './ui/input';
import { Slider } from './ui/slider';
import { Award, Users, MapPinned, MapPin, Loader2, Calendar, DollarSign } from 'lucide-react';

/**
 * The "Filter Options" panel of the Advanced Filter modal (extracted from
 * AdvancedFilterModal.jsx). Purely presentational; all state lives in the parent.
 */
export const FilterOptionsGroup = ({
  filters,
  setFilters,
  sessionTypes = [],
  sportCategories = [],
  genders = [],
  countries = [],
  cities = [],
  citiesLoading = false,
}) => (
  <div className="bg-white rounded-2xl shadow-lg p-5 border-2 border-red-200">
    <h3 className="font-bold text-xl mb-4 text-red-700 flex items-center gap-2">
      <Award className="w-6 h-6" />
      Filter Options
    </h3>

    {/* Trainer Name */}
    <div className="mb-4">
      <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
        <Users className="w-4 h-4 text-red-600" />
        Trainer Name
      </label>
      <Input
        value={filters.trainerName}
        onChange={(e) => setFilters({ ...filters, trainerName: e.target.value })}
        placeholder="Search by name..."
        className="w-full py-3 border-2 border-red-200 focus:border-red-600 rounded-xl"
      />
    </div>

    {/* Select Session Type */}
    <div className="mb-4">
      <label className="block text-sm font-semibold text-gray-700 mb-2">
        Session Type
      </label>
      <select
        value={filters.sessionType}
        onChange={(e) => setFilters({ ...filters, sessionType: e.target.value })}
        className="w-full px-4 py-3 border-2 border-red-200 rounded-xl focus:ring-2 focus:ring-red-600 focus:border-transparent bg-white text-gray-700 font-medium"
      >
        <option value="">All Session Types</option>
        {sessionTypes.map(type => (
          <option key={type} value={type}>{type}</option>
        ))}
      </select>
    </div>

    {/* Select Proficiency */}
    <div className="mb-4">
      <label className="block text-sm font-semibold text-gray-700 mb-2">
        Specialization
      </label>
      <select
        value={filters.proficiency}
        onChange={(e) => setFilters({ ...filters, proficiency: e.target.value })}
        className="w-full px-4 py-3 border-2 border-red-200 rounded-xl focus:ring-2 focus:ring-red-600 focus:border-transparent bg-white text-gray-700 font-medium"
      >
        <option value="">All Specializations</option>
        {sportCategories
          .sort((a, b) => a.name.localeCompare(b.name))
          .map(category => (
            <option key={category.name} value={category.name}>
              {category.icon ? `${category.icon} ${category.name}` : category.name}
            </option>
          ))}
      </select>
    </div>

    {/* Select Gender */}
    <div className="mb-4">
      <label className="block text-sm font-semibold text-gray-700 mb-2">
        Trainer Gender
      </label>
      <select
        value={filters.gender}
        onChange={(e) => setFilters({ ...filters, gender: e.target.value })}
        className="w-full px-4 py-3 border-2 border-red-200 rounded-xl focus:ring-2 focus:ring-red-600 focus:border-transparent bg-white text-gray-700 font-medium"
      >
        <option value="">Any Gender</option>
        {genders.map(gender => (
          <option key={gender} value={gender}>{gender}</option>
        ))}
      </select>
    </div>

    {/* Select Country */}
    <div className="mb-4">
      <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
        <MapPinned className="w-4 h-4 text-red-600" />
        Country
      </label>
      <select
        value={filters.country}
        onChange={(e) => setFilters({ ...filters, country: e.target.value })}
        className="w-full px-4 py-3 border-2 border-red-200 rounded-xl focus:ring-2 focus:ring-red-600 focus:border-transparent bg-white text-gray-700 font-medium"
      >
        <option value="">Select Country</option>
        {countries.map(country => (
          <option key={country} value={country}>{country}</option>
        ))}
      </select>
    </div>

    {/* Select City */}
    <div className="mb-4">
      <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
        <MapPin className="w-4 h-4 text-red-600" />
        City
        {citiesLoading && <Loader2 className="w-3 h-3 animate-spin text-gray-400" />}
      </label>
      <select
        value={filters.city}
        onChange={(e) => setFilters({ ...filters, city: e.target.value })}
        className="w-full px-4 py-3 border-2 border-red-200 rounded-xl focus:ring-2 focus:ring-red-600 focus:border-transparent bg-white text-gray-700 font-medium"
        disabled={citiesLoading}
      >
        <option value="">{citiesLoading ? 'Loading cities...' : 'Select City'}</option>
        {cities.map(city => (
          <option key={city} value={city}>{city}</option>
        ))}
      </select>
    </div>

    {/* Age Range */}
    <div className="mb-6 bg-gradient-to-r from-red-50 to-gray-100 p-4 rounded-xl border-2 border-red-200">
      <label className="block font-semibold mb-3 text-red-700 flex items-center gap-2">
        <Calendar className="w-5 h-5" />
        Age Range
      </label>
      <Slider
        value={filters.ageRange || [18, 70]}
        onValueChange={(value) => setFilters({ ...filters, ageRange: value })}
        min={18}
        max={70}
        step={1}
        className="mb-3"
      />
      <div className="flex justify-between items-center">
        <span className="text-sm font-bold text-red-700 bg-white px-3 py-1 rounded-full">
          {(filters.ageRange && filters.ageRange[0]) || 18} years
        </span>
        <span className="text-xs text-gray-500">to</span>
        <span className="text-sm font-bold text-red-700 bg-white px-3 py-1 rounded-full">
          {(filters.ageRange && filters.ageRange[1]) || 70} years
        </span>
      </div>
    </div>

    {/* Price Range */}
    <div className="mb-6 bg-gradient-to-r from-red-50 to-gray-100 p-4 rounded-xl border-2 border-red-200">
      <label className="block font-semibold mb-3 text-red-700 flex items-center gap-2">
        <DollarSign className="w-5 h-5" />
        Price Range (per hour)
      </label>
      <Slider
        value={filters.priceRange || [30, 150]}
        onValueChange={(value) => setFilters({ ...filters, priceRange: value })}
        min={30}
        max={150}
        step={5}
        className="mb-3"
      />
      <div className="flex justify-between items-center">
        <span className="text-sm font-bold text-red-700 bg-white px-3 py-1 rounded-full">
          ${(filters.priceRange && filters.priceRange[0]) || 30}
        </span>
        <span className="text-xs text-gray-500">to</span>
        <span className="text-sm font-bold text-red-700 bg-white px-3 py-1 rounded-full">
          ${(filters.priceRange && filters.priceRange[1]) || 150}
        </span>
      </div>
    </div>
  </div>
);
